import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
} from '@chakra-ui/react'
import { Button, ChevronLeftIcon, LightModeBox, Menu } from 'components'
import { useWorkspace } from 'components/providers'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { PrimaryLeadProperties, PrimaryLeadProperty, SessionLead } from 'types'
import { uploadFile, useBreakpoint, useIntercomUtils, useScrolling } from 'utils'
import * as XLSX from 'xlsx'
import { useEvent } from '../../../utils/eventUtils'
import PropertyMapper from '../../PropertyMapper'

interface Props {
  isOpen: boolean
  onClose: () => void
  fileBuffer?: ArrayBuffer
}

export default function DatabaseMappingDrawer({ isOpen, onClose, fileBuffer }: Props) {
  useIntercomUtils(isOpen)
  const { event } = useEvent()
  const { workspace } = useWorkspace()
  const [dbTabs, setDbTabs] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState('')
  const [rawDbData, setRawDbData] = useState<Record<string, string>[]>()
  const [dbFields, setDbFields] = useState<Record<string, string>>()
  const [leadMapping, setLeadMapping] = useState<PrimaryLeadProperties>()
  const isMobile = useBreakpoint() < 2
  const dbFilePath = `/events/${event?.id}/attendees.json`

  useEffect(() => {
    if (!fileBuffer) return
    const { SheetNames } = XLSX.read(fileBuffer)
    setDbTabs(SheetNames)
    if (SheetNames.length == 1) setSelectedTab(SheetNames[0])
  }, [fileBuffer])

  useEffect(() => {
    if (!selectedTab) return
    processFile()
  }, [selectedTab])

  async function processFile() {
    const { Sheets } = XLSX.read(fileBuffer)
    const sheet = Sheets[selectedTab]
    const data = XLSX.utils.sheet_to_json(sheet) as Record<string, string>[]
    setRawDbData(data)
    setDbFields(data[0])
  }

  async function saveMapping() {
    if (!rawDbData || !leadMapping) return

    const formatted = rawDbData.map(row => {
      return Object.entries(row).reduce((acc, [key, value]) => {
        const mappedKey = Object.keys(leadMapping).find(
          mappedFieldKey => leadMapping[mappedFieldKey as PrimaryLeadProperty] === key
        )

        if (mappedKey) {
          return { ...acc, [mappedKey]: value }
        } else {
          return {
            ...acc,
            _custom: { ...(acc._custom || {}), [key]: value as string },
          }
        }
      }, {} as SessionLead)
    })

    const jsonFile = new File([JSON.stringify(formatted)], 'attendees.json', {
      type: 'application/json',
    })

    try {
      await uploadFile(`Workspaces/${workspace!.id}${dbFilePath}`, jsonFile)
      handleClose()
    } catch (error) {
      console.error('File upload failed:', error)
    }
  }

  function handleClose() {
    onClose()
    setDbFields(undefined)
    setDbTabs([])
    setSelectedTab('')
    setRawDbData(undefined)
  }

  const { scrolling, overflowRef, contentRef } = useScrolling()

  return (
    <Drawer
      placement="right"
      onClose={handleClose}
      autoFocus={false}
      size={isMobile ? 'xs' : 'lg'}
      isOpen={isOpen}
      closeOnEsc
    >
      <DrawerOverlay />
      <LightModeBox>
        <DrawerContent borderRadius="md" my={2} mr={2}>
          <DrawerHeader borderBottom="1px solid rgba(222, 226, 244, 1)" py={2}>
            <Button
              variant="ghost"
              leftIcon={<ChevronLeftIcon fontSize="lg" />}
              onClick={handleClose}
              pl={0}
            >
              Back
            </Button>

            <DrawerCloseButton h="40px" />
          </DrawerHeader>

          <DrawerBody ref={overflowRef}>
            <Flex ref={contentRef} direction="column">
              {dbTabs && (
                <Flex gap={5} align="center" px={5} py={2}>
                  <Text textStyle="h6">Database tab:</Text>
                  <Menu
                    variant="outline"
                    borderRadius="10px"
                    menuProps={{ matchWidth: true }}
                    value={selectedTab}
                    noPortal
                    options={dbTabs.map(tab => ({ label: tab, value: tab }))}
                    onChange={value => setSelectedTab(value)}
                  />
                </Flex>
              )}
              {dbFields && (
                <PropertyMapper
                  inputFieldLabel="Database Fields"
                  inputMap={dbFields}
                  onChange={setLeadMapping}
                />
              )}
            </Flex>
          </DrawerBody>

          <DrawerFooter borderTop={scrolling ? '1px' : 'none'} borderColor="gray.300">
            <Button palette="primary" isDisabled={isEmpty(leadMapping)} onClick={saveMapping}>
              Save
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </LightModeBox>
    </Drawer>
  )
}
